import { CloseOutlined, DeleteOutlined, EditOutlined, ExperimentOutlined, UndoOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client/react";
import { Alert, App, Button, Flex, Space, Tabs, Typography } from "antd";
import { VersionTag } from "../../../components/VersionTag.tsx";
import { Link, useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { BreadcrumbTrail } from "../../../components/BreadcrumbTrail.tsx";
import { DeleteIngredientDocument, RestoreIngredientDocument } from "../../../gql/generated/graphqlSchema.tsx";
import { useTrailLabel } from "../../../hooks/useTrailLabel.ts";
import { routes } from "../../../routes.ts";
import { useIngredient } from "../IngredientContext.tsx";
import { IngredientTab } from "../constants.ts";
import { IngredientGeneralTab } from "./IngredientGeneralTab.tsx";
import { IngredientNutritionTab } from "./IngredientNutritionTab.tsx";
import { IngredientLabelIngredientsTab } from "./IngredientLabelIngredientsTab.tsx";
import { IngredientLabelTab } from "../IngredientLabelTab.tsx";
import { IngredientPhotosTab } from "./IngredientPhotosTab.tsx";
import { IngredientPriceTab } from "../IngredientPriceTab.tsx";
import { IngredientUsedInRecipesTab } from "../IngredientUsedInRecipesTab.tsx";
import { banner, title } from "../../../styles/shared.styles.ts";

const viewTabSchema = z.enum(IngredientTab).catch(IngredientTab.General);

export const IngredientDetailView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { modal, message } = App.useApp();
  const { ingredient, readOnly, refetch } = useIngredient();
  useTrailLabel(readOnly ? `${ingredient.name} v${ingredient.versionNumber}` : ingredient.name, ExperimentOutlined);

  const [deleteIngredient, { loading: deleting }] = useMutation(DeleteIngredientDocument);
  const [restoreIngredient, { loading: restoring }] = useMutation(RestoreIngredientDocument);

  const activeTab = viewTabSchema.parse(location.hash.replace("#", ""));

  const handleDelete = () => {
    modal.confirm({
      title: t("ingredients.deleteTitle"),
      content: t("ingredients.deleteConfirm"),
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      cancelButtonProps: { icon: <CloseOutlined /> },
      okText: t("ingredients.delete"),
      onOk: async () => {
        await deleteIngredient({ variables: { id: ingredient.id } });
        void message.success(t("ingredients.deletedDone"));
        void refetch();
      },
    });
  };

  const handleRestore = () => {
    modal.confirm({
      title: t("ingredients.restoreTitle"),
      content: t("ingredients.restoreConfirm"),
      okButtonProps: { icon: <UndoOutlined /> },
      cancelButtonProps: { icon: <CloseOutlined /> },
      okText: t("ingredients.restore"),
      onOk: async () => {
        await restoreIngredient({ variables: { id: ingredient.id } });
        void message.success(t("ingredients.restoredDone"));
        void refetch();
      },
    });
  };

  return (
    <Flex gap="middle" vertical>
      <Flex gap="small" vertical>
        <BreadcrumbTrail />
        <Space wrap>
          <Typography.Title className={title} level={3}>
            {ingredient.name}
          </Typography.Title>
          <VersionTag entity={ingredient} />
        </Space>
        {ingredient.deleted ? (
          <Alert
            action={
              <Button icon={<UndoOutlined />} loading={restoring} onClick={handleRestore}>
                {t("ingredients.restore")}
              </Button>
            }
            className={banner}
            showIcon
            title={t("ingredients.deletedBanner")}
            type="error"
          />
        ) : readOnly ? (
          <Alert
            action={
              <Link to={routes.ingredientDetail.to(ingredient.id)}>{t("ingredients.archivedVersionBannerAction")}</Link>
            }
            className={banner}
            showIcon
            title={t("ingredients.archivedVersionBannerText")}
            type="warning"
          />
        ) : ingredient.hasDraft ? (
          <Alert
            action={
              <Button
                icon={<EditOutlined />}
                onClick={() =>
                  void navigate({ pathname: routes.ingredientDetailEdit.to(ingredient.id), hash: location.hash })
                }
                type="primary"
              >
                {t("ingredients.continueEditing")}
              </Button>
            }
            className={banner}
            showIcon
            title={t("ingredients.draftBannerText")}
            type="info"
          />
        ) : (
          <Space wrap>
            <Button
              icon={<EditOutlined />}
              onClick={() =>
                void navigate({ pathname: routes.ingredientDetailEdit.to(ingredient.id), hash: location.hash })
              }
              type="primary"
            >
              {t("ingredients.edit")}
            </Button>
            <Button danger icon={<DeleteOutlined />} loading={deleting} onClick={handleDelete}>
              {t("ingredients.delete")}
            </Button>
          </Space>
        )}
      </Flex>

      <Tabs
        activeKey={activeTab}
        destroyOnHidden
        items={[
          { key: IngredientTab.General, label: t("ingredients.tabGeneral"), children: <IngredientGeneralTab /> },
          {
            key: IngredientTab.LabelIngredients,
            label: t("ingredients.tabLabelIngredients", { count: ingredient.labelIngredients.length }),
            children: <IngredientLabelIngredientsTab />,
          },
          { key: IngredientTab.Nutrition, label: t("ingredients.tabNutrition"), children: <IngredientNutritionTab /> },
          { key: IngredientTab.Label, label: t("ingredients.tabLabel"), children: <IngredientLabelTab /> },
          {
            key: IngredientTab.Photos,
            label: t("ingredients.tabPhotos", { count: ingredient.photos.length }),
            children: <IngredientPhotosTab />,
          },
          {
            key: IngredientTab.Price,
            label: t("ingredients.tabPrice"),
            children: <IngredientPriceTab />,
          },
          {
            key: IngredientTab.UsedInRecipes,
            label: t("ingredients.tabUsedInRecipes", { count: ingredient.usedInRecipes.length }),
            children: <IngredientUsedInRecipesTab />,
          },
        ]}
        onChange={(tab) => void navigate({ hash: tab }, { replace: true })}
      />
    </Flex>
  );
};
